import React, { useEffect, useState, useMemo } from 'react';
import { SecondaryBase } from './../Commons/SecondaryBase';
import { ResetCurrentDynamic as BLResetCurrentDynamic, SetAutozoneState as BLSetAutozoneState } from './../../../utilities/BL/Zones';
import { MESSAGE_TYPE, useFloatMessage } from './../../Providers/FloatMessage';
import { Prepare as BLPrepare, Start as BLStart, Stop as BLStop, GetAllPresets, CreatePreset, UpdatePreset, DeletePreset } from './../../../utilities/BL/Admin/Games/LightShow';
import { Confirm } from './../../Commons/Modals/Confirm';
import { ValidateSqlInjection } from './../../../utilities/BL/Security';
import { SetScreenV2MainDynamic } from './../../../utilities/BL/ScreenV2';


const LightShow = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();

    // ==========================================
    // ESTADOS DEL JUEGO (LUCES)
    // ==========================================
    const [beatsPerMinute, setBeatsPerMinute] = useState(118);
    const [currentSystemStatus, setCurrentSystemStatus] = useState('Esperando. Configura el ritmo y colores.');
    const [animationFrames, setAnimationFrames] = useState([{ id: 1, color: '#533278', flashOn: true }, { id: 2, color: '#000000', flashOn: false }]);
    const [isSystemWorking, setIsSystemWorking] = useState(false);
    const [showAdvancedConfig, setShowAdvancedConfig] = useState(false);

    // ==========================================
    // ESTADOS DE PRESETS
    // ==========================================
    const [presetsDb, setPresetsDb] = useState([]);
    const [activePreset, setActivePreset] = useState(null);
    const [isLoadingPresets, setIsLoadingPresets] = useState(true);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalData, setConfirmModalData] = useState(null);

    // ==========================================
    // ESTADOS DE ESTADÍSTICAS EN VIVO
    // ==========================================
    const [liveStadiumStats, setLiveStadiumStats] = useState(null);

    // ==========================================
    // EFECTOS INICIALES
    // ==========================================
    const loadPresetsFromDb = async () => {
        setIsLoadingPresets(true);
        const res = await GetAllPresets();
        if (res.success) {
            setPresetsDb(res.data);
            if (!activePreset && res.data.length > 0) {
                applyPresetToUI(res.data[0]);
            }
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: res.message });
        }
        setIsLoadingPresets(false);
    };

    useEffect(() => {
        loadPresetsFromDb();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const fetchStadiumLiveStats = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}Stadium/LiveStats`);
                if (response.ok) setLiveStadiumStats(await response.json());
            } catch (error) { }
        };
        fetchStadiumLiveStats();
        const pollingInterval = setInterval(fetchStadiumLiveStats, 3000);
        return () => clearInterval(pollingInterval);
    }, []);

    const hasUnsavedChanges = useMemo(() => {
        if (!activePreset) return true;
        if (activePreset.beatsPerMinute !== beatsPerMinute) return true;
        try {
            const currentSimple = animationFrames.map(f => ({ c: f.color.toLowerCase(), f: f.flashOn }));
            const savedDbFrames = JSON.parse(activePreset.framesJson);
            const savedSimple = savedDbFrames.map(f => ({ c: (f.color||'').toLowerCase(), f: f.flashOn }));
            return JSON.stringify(currentSimple) !== JSON.stringify(savedSimple);
        } catch (e) { return true; }
    }, [activePreset, beatsPerMinute, animationFrames]);

    // ==========================================
    // MÉTODOS DE PRESETS
    // ==========================================
    const applyPresetToUI = (preset) => {
        try {
            const parsedFrames = JSON.parse(preset.framesJson);
            setAnimationFrames(parsedFrames);
            setBeatsPerMinute(preset.beatsPerMinute);
            setActivePreset(preset);
        } catch (e) { showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al leer los colores de este preset.' }); }
    };

    const handleSaveUpdate = async () => {
        if (!activePreset) return { success: false, message: 'No hay preset activo.' };
        const res = await UpdatePreset(activePreset.id, activePreset.name, beatsPerMinute, animationFrames);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: res.message });
            setActivePreset(prev => ({ ...prev, beatsPerMinute, framesJson: JSON.stringify(animationFrames) }));
            await loadPresetsFromDb(); 
            return { success: true };
        } else {
            return { success: false, message: res.message };
        }
    };

    const handleSaveAsNew = async () => {
        const newName = window.prompt("Ingresa un nombre para tu preset personalizado:");
        if (!newName || newName.trim() === '') return;
        const isMalicious = await ValidateSqlInjection(newName);
        if (isMalicious) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Nombre inválido por seguridad.' });
            return;
        }
        const res = await CreatePreset(newName, beatsPerMinute, animationFrames);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: res.message });
            await loadPresetsFromDb();
            applyPresetToUI(res.data);
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: res.message });
        }
    };

    const handleDeletePreset = async () => {
        if (!activePreset) return { success: false, message: 'No hay preset seleccionado.' };
        if (activePreset.isSystemPreset) return { success: false, message: 'No se puede eliminar un preset del sistema.' };
        const res = await DeletePreset(activePreset.id);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: res.message });
            setActivePreset(null);
            await loadPresetsFromDb();
            return { success: true };
        } else {
            return { success: false, message: res.message };
        }
    };

    const handleRestoreDefault = async () => {
        if (activePreset) applyPresetToUI(activePreset);
        return { success: true };
    };

    const addFrame = () => {
        const lastFrame = animationFrames.length > 0 ? animationFrames[animationFrames.length - 1] : null;
        const newFrame = { id: Date.now(), color: '#533278', flashOn: lastFrame ? !lastFrame.flashOn : true };
        setAnimationFrames([...animationFrames, newFrame]);
    };

    const removeFrame = (frameId) => {
        if (animationFrames.length <= 2) return showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Debes tener al menos 2 frames.' });
        setAnimationFrames(animationFrames.filter(frame => frame.id !== frameId));
    };

    const updateFrame = (id, prop, val) => setAnimationFrames(prev => prev.map(f => f.id === id ? { ...f, [prop]: val } : f));
    const moveFrameUp = (i) => { if (i===0) return; const arr = [...animationFrames]; [arr[i-1], arr[i]] = [arr[i], arr[i-1]]; setAnimationFrames(arr); };
    const moveFrameDown = (i) => { if (i===animationFrames.length-1) return; const arr = [...animationFrames]; [arr[i+1], arr[i]] = [arr[i], arr[i+1]]; setAnimationFrames(arr); };

    // ==========================================================================
    // OPERACIÓN UNIFICADA (FASES)
    // ==========================================================================
    
    // FASE 1: ABRIR SALA (Con Asignación AUTOMÁTICA)
    const handleOpenRoomAndShowQR = async () => {
        try {
            setIsSystemWorking(true);
            showMessage({ type: MESSAGE_TYPE.INFO, text: "Cambiando a Zonas Automáticas y limpiando..." });

            // 1. Forzamos Asignación Automática
            await BLSetAutozoneState(true);

            // 2. Limpiamos la pantalla gigante a su Idle
            await BLResetCurrentDynamic(); 
            
            // 3. Mandamos QR a la pantalla
            await fetch(`${process.env.REACT_APP_API_URL}Admin/sendWelcome`, { method: 'POST' });

            // 4. Creamos ronda y purgamos base de datos
            await fetch(`${process.env.REACT_APP_API_URL}Stadium/StartNewRound`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ roundName: 'Show de Luces' })
            });

            // 5. Limpiamos V2 (opcional, por si había algo flotando)
            await SetScreenV2MainDynamic(1, null);

            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Modo Automático/Femenil activo. Sala abierta y QR en pantalla." });
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Error en la preparación." });
        } finally {
            setIsSystemWorking(false);
        }
    };

    // BOTONES SECUNDARIOS (Emergencias)
    const handleEmergencyPurgeOnly = async () => {
        try {
            setIsSystemWorking(true);
            await fetch(`${process.env.REACT_APP_API_URL}Stadium/StartNewRound`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ roundName: 'Show de Luces (Purga)' })
            });
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Conexiones purgadas exitosamente." });
        } catch (e) { showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al purgar." }); } finally { setIsSystemWorking(false); }
    };

    const handleEmergencyQROnly = async () => {
        try {
            setIsSystemWorking(true);
            await fetch(`${process.env.REACT_APP_API_URL}Admin/sendWelcome`, { method: 'POST' });
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "QR reenviado a la pantalla." });
        } catch (e) { showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al mostrar QR." }); } finally { setIsSystemWorking(false); }
    };


    // FASE 2: OPERACIÓN
    const executeQuickStartShow = async () => {
        setCurrentSystemStatus('Iniciando proceso...');
        
        const prepResponse = await BLPrepare(beatsPerMinute, animationFrames);
        if (!prepResponse?.success) {
            setCurrentSystemStatus('Error en preparación.');
            return { success: false, message: prepResponse?.message };
        }

        const startResponse = await BLStart(beatsPerMinute, animationFrames);
        if (!startResponse?.success) {
            setCurrentSystemStatus('Error al iniciar.');
            return { success: false, message: startResponse?.message };
        }

        setCurrentSystemStatus(`¡SHOW EN CURSO! 🌟 (${beatsPerMinute} BPM)`);
        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Luces corriendo exitosamente.' });
        return { success: true };
    };

    const executeStopShow = async () => {
        const response = await BLStop();
        if (response?.success) {
            setCurrentSystemStatus('Show Detenido 🛑');
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Luces apagadas.' });
            return { success: true };
        }
        return { success: false, message: response?.message };
    };

    const triggerModal = (msg, action, btnText) => {
        setConfirmModalData({ message: msg, actionToYesButton: action, textYesButton: btnText, textNoButton: 'Cancelar' });
        setShowConfirmModal(true);
    };

    const ReturnToDashboard = async () => {
        await BLResetCurrentDynamic();
        getOutOfTheDynamic();
    };

    // ==========================================================================
    // RENDERIZADO
    // ==========================================================================
    return (
        <SecondaryBase actionToExecute={ReturnToDashboard}>
            <section className='AdmRunToGoal__Settings pb-5'>
                
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center border-bottom pb-2 mb-4">
                    <p className='AdmRunToGoal__Title mb-0'>
                        <i className='bi bi-phone-vibrate me-2'></i> Show de Luces (Unificado)
                    </p>
                    <div className="alert alert-dark py-2 px-3 mb-0 fw-bold shadow-sm" style={{ fontSize: '0.9rem' }}>
                        <i className="bi bi-broadcast-pin me-2 text-warning"></i> Status: {currentSystemStatus}
                    </div>
                </div>

                <div className="row g-4">
                    {/* =======================================================================
                        COLUMNA IZQUIERDA: CONFIGURADOR DE COLORES (PRESETS)
                    ======================================================================= */}
                    <div className="col-md-7 mb-4">
                        <div className="card shadow-sm h-100 border-dark">
                            <div className="card-header bg-dark text-white p-3">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <span className="fw-bold"><i className="bi bi-palette-fill me-2"></i> Diseño Visual</span>
                                    <div className="d-flex align-items-center">
                                        <label className="mb-0 me-2 small fw-bold">BPM (Ritmo):</label>
                                        <input 
                                            type="number" className="form-control form-control-sm text-center fw-bold text-dark" 
                                            style={{ width: '80px' }} value={beatsPerMinute} 
                                            onChange={(e) => setBeatsPerMinute(parseInt(e.target.value) || 0)} 
                                        />
                                    </div>
                                </div>
                                
                                {/* SELECTOR DE PRESETS */}
                                <div className="bg-secondary p-2 rounded d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
                                    <div>
                                        <span className="small text-light">Preset:</span>
                                        <span className={`ms-2 badge ${activePreset?.isSystemPreset ? 'bg-dark text-light' : 'bg-primary'} fs-6`}>
                                            {activePreset ? activePreset.name : 'Personalizado'}
                                        </span>
                                        {hasUnsavedChanges && <span className="ms-2 text-warning fw-bold small">* Editado</span>}
                                    </div>
                                    <div className="d-flex flex-wrap gap-2">
                                        {activePreset && hasUnsavedChanges && (
                                            <button className="btn btn-sm btn-success fw-bold shadow-sm" 
                                                onClick={() => triggerModal(`¿Guardar y sobrescribir "${activePreset.name}"?`, handleSaveUpdate, 'Guardar cambios')}>
                                                <i className="bi bi-floppy-fill me-1"></i> Guardar
                                            </button>
                                        )}
                                        {hasUnsavedChanges && activePreset && (
                                            <button className="btn btn-sm btn-danger" 
                                                onClick={() => triggerModal(`¿Descartar cambios de "${activePreset.name}"?`, handleRestoreDefault, 'Sí, descartar')}>
                                                Descartar
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="card-body p-0">
                                <table className="table table-bordered table-hover mb-0 align-middle text-center">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{width: '60px'}}>Orden</th>
                                            <th>Color</th>
                                            <th>¿Flash activo?</th>
                                            <th style={{width: '60px'}}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {animationFrames.map((frame, index) => (
                                            <tr key={frame.id}>
                                                <td>
                                                    <div className="d-flex flex-column align-items-center">
                                                        <button className="btn btn-sm text-muted p-0 border-0" onClick={() => moveFrameUp(index)} disabled={index===0}><i className="bi bi-caret-up-fill"></i></button>
                                                        <span className="fw-bold small">{index + 1}</span>
                                                        <button className="btn btn-sm text-muted p-0 border-0" onClick={() => moveFrameDown(index)} disabled={index===animationFrames.length-1}><i className="bi bi-caret-down-fill"></i></button>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex justify-content-center align-items-center">
                                                        <input 
                                                            type="color" className="form-control form-control-color border-0 p-0 me-2" 
                                                            value={frame.color} onChange={(e) => updateFrame(frame.id, 'color', e.target.value)} 
                                                            style={{ width: '40px', height: '30px' }} 
                                                        />
                                                        <span className="small text-muted font-monospace">{frame.color.toUpperCase()}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="form-check form-switch d-flex justify-content-center">
                                                        <input 
                                                            className="form-check-input" type="checkbox" checked={frame.flashOn} 
                                                            onChange={(e) => updateFrame(frame.id, 'flashOn', e.target.checked)} 
                                                            style={{ cursor: 'pointer', width: '40px', height: '20px' }} 
                                                        />
                                                    </div>
                                                </td>
                                                <td>
                                                    <button className="btn btn-sm btn-outline-danger" onClick={() => removeFrame(frame.id)} title="Borrar Frame">
                                                        <i className="bi bi-trash3-fill"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            
                            <div className="card-footer bg-light">
                                <button className="btn btn-sm btn-outline-success fw-bold mb-3" onClick={addFrame}>
                                    <i className="bi bi-plus-circle-fill me-1"></i> Agregar Frame
                                </button>
                                <hr className="my-2"/>
                                <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                                    <span className="text-muted small fw-bold">Cargar:</span>
                                    {isLoadingPresets ? (
                                        <span className="small text-muted">Cargando...</span>
                                    ) : presetsDb.length <= 10 ? (
                                        presetsDb.map(p => (
                                            <button key={p.id} className={`btn btn-sm ${activePreset?.id === p.id ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => applyPresetToUI(p)}>
                                                {p.name}
                                            </button>
                                        ))
                                    ) : (
                                        <select className="form-select form-select-sm w-auto" value={activePreset?.id || ''} onChange={(e) => applyPresetToUI(presetsDb.find(p => p.id === parseInt(e.target.value)))}>
                                            <option value="" disabled>-- Selecciona un Preset --</option>
                                            {presetsDb.map(p => (
                                                <option key={p.id} value={p.id}>{p.name} {p.isSystemPreset ? '(Sistema)' : ''}</option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                                <div className="d-flex flex-wrap gap-2 mt-3">
                                    <button className="btn btn-sm btn-light fw-bold" onClick={handleSaveAsNew}>
                                        <i className="bi bi-plus-circle text-primary me-1"></i> Guardar como nuevo
                                    </button>
                                    {activePreset && !activePreset.isSystemPreset && (
                                        <button className="btn btn-sm btn-outline-danger ms-auto" onClick={() => triggerModal(`¿Eliminar preset "${activePreset.name}"?`, handleDeletePreset, 'Eliminar')}>
                                            <i className="bi bi-trash"></i> Eliminar
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* =======================================================================
                        COLUMNA DERECHA: MASTER CONTROL Y FASES UNIFICADAS
                    ======================================================================= */}
                    <div className="col-md-5 mb-4">
                        <div className="card shadow-sm h-100 border-primary">
                            <div className="card-header bg-primary text-white fw-bold">
                                <i className="bi bi-joystick me-2"></i> Operación del Estadio
                            </div>
                            
                            <div className="card-body bg-light">
                                {/* FASE 1 */}
                                <div className="mb-4 border-bottom pb-4">
                                    <p className="fw-bold text-muted small mb-2">FASE 1: PREPARACIÓN</p>
                                    <button
                                        className={`btn btn-danger fw-bold w-100 py-3 shadow-sm ${isSystemWorking ? 'disabled' : ''}`}
                                        onClick={handleOpenRoomAndShowQR}
                                        disabled={isSystemWorking}
                                    >
                                        <i className="bi bi-robot me-1"></i> 1. Preparar Sala (Asignación Automática)
                                    </button>
                                    <small className="text-muted d-block mt-2 lh-sm" style={{ fontSize: '0.75rem' }}>
                                        * Ajusta el servidor a "Zonas Automáticas", desconecta inactivos y lanza el QR a pantalla para acceso directo.
                                    </small>
                                </div>

                                {/* FASE 2 */}
                                <div>
                                    <p className="fw-bold text-muted small mb-2">FASE 2: OPERACIÓN EN VIVO</p>
                                    <button className="btn btn-success mb-3 w-100 py-3 fw-bold shadow-sm" 
                                        onClick={() => triggerModal('¿Iniciar show con los parámetros actuales?', executeQuickStartShow, 'Sí, iniciar')}>
                                        <i className="bi bi-play-fill me-1"></i> Iniciar / Actualizar Show
                                    </button>

                                    <button className="btn btn-outline-danger fw-bold w-100" 
                                        onClick={() => triggerModal('¿Detener el show y apagar celulares?', executeStopShow, 'Sí, detener show')}>
                                        <i className="bi bi-stop-circle-fill me-1"></i> Detener / Apagar Luces
                                    </button>
                                </div>

                                {/* CONFIGURACIÓN AVANZADA Y EMERGENCIAS */}
                                <div className="mt-4 text-end">
                                    <button 
                                        className="btn btn-sm btn-link text-muted text-decoration-none"
                                        onClick={() => setShowAdvancedConfig(!showAdvancedConfig)}
                                    >
                                        <i className={`bi bi-gear${showAdvancedConfig ? '-fill' : ''}`}></i> Herramientas de Emergencia
                                    </button>
                                    
                                    {showAdvancedConfig && (
                                        <div className="bg-white p-3 rounded border text-start mt-2 shadow-sm transition">
                                            <p className="small fw-bold text-danger mb-2">Acciones individuales:</p>
                                            <div className="d-flex flex-column gap-2">
                                                <button className="btn btn-sm btn-outline-secondary text-start" onClick={handleEmergencyPurgeOnly} disabled={isSystemWorking}>
                                                    <i className="bi bi-eraser-fill me-2"></i> Solo Purgar Conexiones
                                                </button>
                                                <button className="btn btn-sm btn-outline-secondary text-start" onClick={handleEmergencyQROnly} disabled={isSystemWorking}>
                                                    <i className="bi bi-qr-code me-2"></i> Solo Enviar QR a Pantalla
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* =======================================================================
                    ESTADÍSTICAS EN VIVO
                ======================================================================= */}
                <div className='mt-3 mb-5 border-top pt-4'>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="fw-bold mb-0">Celulares Iluminando el Estadio</h5>
                        {liveStadiumStats && <span className="badge bg-secondary">{liveStadiumStats.roundName}</span>}
                    </div>

                    {liveStadiumStats ? (
                        <div className="row text-center mb-4 g-3">
                            <div className="col-sm-6">
                                <div className="p-4 bg-light rounded border border-success border-opacity-25 shadow-sm">
                                    <h1 className="fw-black text-success mb-0" style={{ fontSize: '3rem' }}>{liveStadiumStats.totalCurrentlyActive}</h1>
                                    <span className="fw-bold text-muted text-uppercase">Cámaras Activas Ahora</span>
                                </div>
                            </div>
                            <div className="col-sm-6">
                                <div className="p-4 bg-light rounded border shadow-sm">
                                    <h2 className="fw-black text-secondary mb-0">{liveStadiumStats.totalEverConnected}</h2>
                                    <span className="small text-muted text-uppercase">Total Histórico</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center p-4 text-muted">
                            <div className="spinner-border mb-3" role="status"></div>
                            <p>Cargando datos del estadio...</p>
                        </div>
                    )}
                </div>

            </section>

            {showConfirmModal && (
                <Confirm 
                    message={confirmModalData?.message} 
                    actionToYesButton={confirmModalData?.actionToYesButton} 
                    textYesButton={confirmModalData?.textYesButton} 
                    textNoButton={confirmModalData?.textNoButton} 
                    setShowModal={setShowConfirmModal} 
                    setConfirmData={setConfirmModalData} 
                />
            )}
        </SecondaryBase>
    );
};

export { LightShow };










// import { useEffect, useState, useMemo } from 'react'
// import { SecondaryBase } from './../Commons/SecondaryBase'
// import { ResetCurrentDynamic as BLResetCurrentDynamic } from './../../../utilities/BL/Zones'
// import { MESSAGE_TYPE, useFloatMessage } from './../../Providers/FloatMessage'
// import { Prepare as BLPrepare, Start as BLStart, Stop as BLStop, GetAllPresets, CreatePreset, UpdatePreset, DeletePreset } from './../../../utilities/BL/Admin/Games/LightShow'
// import { Confirm } from './../../Commons/Modals/Confirm'
// import { ValidateSqlInjection } from './../../../utilities/BL/Security' // <--- IMPORTACIÓN DE SEGURIDAD

// const LightShow = ({ getOutOfTheDynamic }) => {
//     const { showMessage } = useFloatMessage()

//     const [beatsPerMinute, setBeatsPerMinute] = useState(118);
//     const [currentSystemStatus, setCurrentSystemStatus] = useState('Juego inactivo. Configura el ritmo y colores.');
//     const [animationFrames, setAnimationFrames] = useState([{ id: 1, color: '#533278', flashOn: true }, { id: 2, color: '#000000', flashOn: false }]);

//     // ==========================================
//     // ESTADOS DE PRESETS (Base de Datos)
//     // ==========================================
//     const [presetsDb, setPresetsDb] = useState([]);
//     const [activePreset, setActivePreset] = useState(null);
//     const [isLoadingPresets, setIsLoadingPresets] = useState(true);

//     const [showConfirmModal, setShowConfirmModal] = useState(false);
//     const [confirmModalData, setConfirmModalData] = useState(null);

//     // ==========================================
//     // CARGA INICIAL DESDE BD
//     // ==========================================
//     const loadPresetsFromDb = async () => {
//         setIsLoadingPresets(true);
//         const res = await GetAllPresets();
//         if (res.success) {
//             setPresetsDb(res.data);
//             // Autocargar el primer preset si no hay nada activo
//             if (!activePreset && res.data.length > 0) {
//                 applyPresetToUI(res.data[0]);
//             }
//         } else {
//             showMessage({ type: MESSAGE_TYPE.BAD, text: res.message });
//         }
//         setIsLoadingPresets(false);
//     };

//     useEffect(() => {
//         loadPresetsFromDb();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []);

//     // ==========================================
//     // INDICADOR DE "CAMBIOS SIN GUARDAR"
//     // ==========================================
//     const hasUnsavedChanges = useMemo(() => {
//         if (!activePreset) return true;
//         if (activePreset.beatsPerMinute !== beatsPerMinute) return true;
        
//         try {
//             const currentSimple = animationFrames.map(f => ({ c: f.color.toLowerCase(), f: f.flashOn }));
//             const savedDbFrames = JSON.parse(activePreset.framesJson);
//             const savedSimple = savedDbFrames.map(f => ({ c: (f.color||'').toLowerCase(), f: f.flashOn }));
//             return JSON.stringify(currentSimple) !== JSON.stringify(savedSimple);
//         } catch (e) {
//             return true;
//         }
//     }, [activePreset, beatsPerMinute, animationFrames]);

//     // ==========================================
//     // MANEJO DE PRESETS EN UI
//     // ==========================================
//     const applyPresetToUI = (preset) => {
//         try {
//             const parsedFrames = JSON.parse(preset.framesJson);
//             setAnimationFrames(parsedFrames);
//             setBeatsPerMinute(preset.beatsPerMinute);
//             setActivePreset(preset);
//         } catch (e) {
//             showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al leer los colores de este preset.' });
//         }
//     };

//     // DEBE retornar { success: true } para que el Modal Confirm se cierre.
//     const handleSaveUpdate = async () => {
//         if (!activePreset) return { success: false, message: 'No hay preset activo.' };
        
//         const res = await UpdatePreset(activePreset.id, activePreset.name, beatsPerMinute, animationFrames);
//         if (res.success) {
//             showMessage({ type: MESSAGE_TYPE.SUCCESS, text: res.message });
//             // Actualizamos la referencia local para que el asterisco de "Sin Guardar" desaparezca
//             setActivePreset(prev => ({ ...prev, beatsPerMinute, framesJson: JSON.stringify(animationFrames) }));
//             await loadPresetsFromDb(); 
//             return { success: true }; // <--- Cierra el modal
//         } else {
//             return { success: false, message: res.message }; // <--- Muestra error en el modal
//         }
//     };

//     // No usa Modal Confirm, porque el window.prompt bloquea nativamente
//     const handleSaveAsNew = async () => {
//         const newName = window.prompt("Ingresa un nombre para tu preset personalizado:");
//         if (!newName || newName.trim() === '') return;

//         // Validar inyección SQL
//         const isMalicious = await ValidateSqlInjection(newName);
//         if (isMalicious) {
//             showMessage({ type: MESSAGE_TYPE.BAD, text: 'El nombre ingresado contiene caracteres o palabras no permitidas por seguridad.' });
//             return;
//         }

//         const res = await CreatePreset(newName, beatsPerMinute, animationFrames);
//         if (res.success) {
//             showMessage({ type: MESSAGE_TYPE.SUCCESS, text: res.message });
//             await loadPresetsFromDb();
//             applyPresetToUI(res.data);
//         } else {
//             showMessage({ type: MESSAGE_TYPE.BAD, text: res.message });
//         }
//     };

//     // DEBE retornar { success: true } para que el Modal Confirm se cierre.
//     const handleDeletePreset = async () => {
//         if (!activePreset) return { success: false, message: 'No hay preset seleccionado.' };
//         if (activePreset.isSystemPreset) return { success: false, message: 'No se puede eliminar un preset del sistema.' };

//         const res = await DeletePreset(activePreset.id);
//         if (res.success) {
//             showMessage({ type: MESSAGE_TYPE.SUCCESS, text: res.message });
//             setActivePreset(null);
//             await loadPresetsFromDb();
//             return { success: true };
//         } else {
//             return { success: false, message: res.message };
//         }
//     };

//     // AGREGAMOS el async y el return { success: true } para que cierre el modal
//     const handleRestoreDefault = async () => {
//         if (activePreset) {
//             applyPresetToUI(activePreset);
//         }
//         return { success: true };
//     };

//     // ==========================================
//     // MÉTODOS DEL CONSTRUCTOR DE FRAMES
//     // ==========================================
//     const addFrame = () => {
//         // Lee el último frame para alternar el prendido/apagado
//         const lastFrame = animationFrames.length > 0 ? animationFrames[animationFrames.length - 1] : null;
//         const nextFlashState = lastFrame ? !lastFrame.flashOn : true;

//         const newFrame = { id: Date.now(), color: '#533278', flashOn: nextFlashState };
//         setAnimationFrames([...animationFrames, newFrame]);
//     };

//     const removeFrame = (frameIdToRemove) => {
//         if (animationFrames.length <= 2) {
//             showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Debes tener al menos 2 frames.' });
//             return;
//         }
//         setAnimationFrames(animationFrames.filter(frame => frame.id !== frameIdToRemove));
//     };

//     const updateFrame = (frameIdToUpdate, propertyName, newValue) => {
//         setAnimationFrames(prev => prev.map(frame => frame.id === frameIdToUpdate ? { ...frame, [propertyName]: newValue } : frame));
//     };

//     const moveFrameUp = (index) => {
//         if (index === 0) return;
//         const newFrames = [...animationFrames];
//         [newFrames[index - 1], newFrames[index]] = [newFrames[index], newFrames[index - 1]];
//         setAnimationFrames(newFrames);
//     };

//     const moveFrameDown = (index) => {
//         if (index === animationFrames.length - 1) return;
//         const newFrames = [...animationFrames];
//         [newFrames[index + 1], newFrames[index]] = [newFrames[index], newFrames[index + 1]];
//         setAnimationFrames(newFrames);
//     };

//     // ==========================================
//     // MÉTODOS DE LA BATUTA (Show en vivo)
//     // ==========================================
    
//     // DEBE retornar { success: true } para que el Modal Confirm se cierre.
//     const executeQuickStartShow = async () => {
//         setCurrentSystemStatus('Iniciando proceso...');
        
//         const prepResponse = await BLPrepare(beatsPerMinute, animationFrames);
//         if (!prepResponse?.success) {
//             setCurrentSystemStatus('Error en la preparación.');
//             return { success: false, message: prepResponse?.message };
//         }

//         const startResponse = await BLStart(beatsPerMinute, animationFrames);
//         if (!startResponse?.success) {
//             setCurrentSystemStatus('Error al iniciar el show.');
//             return { success: false, message: startResponse?.message };
//         }

//         setCurrentSystemStatus(`¡SHOW EN CURSO! 🌟 (${beatsPerMinute} BPM)`);
//         showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'El show de luces está corriendo exitosamente' });
//         return { success: true }; // <--- Cierra el modal
//     };

//     // DEBE retornar { success: true } para que el Modal Confirm se cierre.
//     const executeStopShow = async () => {
//         const response = await BLStop();

//         if (response?.success) {
//             setCurrentSystemStatus('Show Detenido 🛑');
//             showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Luces apagadas exitosamente.' });
//             return { success: true }; // <--- Cierra el modal
//         }

//         return { success: false, message: response?.message }; // <--- Muestra error en el modal
//     };

//     const triggerModal = (messageToDisplay, actionFunction, yesButtonText) => {
//         setConfirmModalData({ message: messageToDisplay, actionToYesButton: actionFunction, textYesButton: yesButtonText, textNoButton: 'Cancelar' });
//         setShowConfirmModal(true);
//     };

//     const ReturnToDashboard = async () => {
//         await BLResetCurrentDynamic();
//         getOutOfTheDynamic();
//     };

//     return (
//         <SecondaryBase actionToExecute={ReturnToDashboard}>
//             <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4">
//                 <p className='AdmRunToGoal__Title mb-3 mb-md-0'>
//                     <i className='bi bi-phone-vibrate me-2'></i> Show de Luces
//                 </p>
//                 <div className="alert alert-dark py-2 px-3 mb-0 font-weight-bold shadow-sm" style={{ fontSize: '0.9rem' }}>
//                     <i className="bi bi-broadcast-pin me-2"></i> Status: {currentSystemStatus}
//                 </div>
//             </div>

//             <div className="row">
//                 <div className="col-md-7 mb-4">
//                     <div className="card shadow-sm h-100 border-dark">
                        
//                         {/* ============================================== */}
//                         {/* CABECERA REDISEÑADA (Más intuitiva y arriba) */}
//                         {/* ============================================== */}
//                         <div className="card-header bg-dark text-white p-3">
//                             <div className="d-flex justify-content-between align-items-center mb-3">
//                                 <span className="font-weight-bold"><i className="bi bi-palette-fill me-2"></i> 1. Ciclo de Colores</span>
//                                 <div className="d-flex align-items-center">
//                                     <label className="mb-0 me-2 small fw-bold">BPM (Ritmo):</label>
//                                     <input 
//                                         type="number" className="form-control form-control-sm text-center font-weight-bold text-dark" 
//                                         style={{ width: '80px' }} value={beatsPerMinute} 
//                                         onChange={(e) => setBeatsPerMinute(parseInt(e.target.value) || 0)} 
//                                     />
//                                 </div>
//                             </div>
                            
//                             {/* PANEL DE PRESET ACTIVO */}
//                             <div className="bg-secondary p-2 rounded d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
//                                 <div>
//                                     <span className="small text-light">Preset:</span>
//                                     <span className={`ms-2 badge ${activePreset?.isSystemPreset ? 'bg-dark text-light' : 'bg-primary'} fs-6`}>
//                                         {activePreset ? activePreset.name : 'Personalizado'}
//                                         {/* {activePreset?.isSystemPreset && <i className="bi bi-shield-lock-fill ms-2" title="Sistema"></i>} */}
//                                     </span>
//                                     {hasUnsavedChanges && <span className="ms-2 text-warning font-weight-bold small">* Editado</span>}
//                                 </div>
//                                 <div className="d-flex flex-wrap gap-2">
//                                     {activePreset && hasUnsavedChanges && (
//                                         <button className="btn btn-sm btn-success font-weight-bold shadow-sm" 
//                                             // INYECCIÓN DE NOMBRE DINÁMICO
//                                             onClick={() => triggerModal(`¿Deseas guardar los cambios y sobrescribir el preset "${activePreset.name}"?`, handleSaveUpdate, 'Guardar cambios')}>
//                                             <i className="bi bi-floppy-fill me-1"></i> Guardar cambios
//                                         </button>
//                                     )}

//                                     {hasUnsavedChanges && activePreset && (
//                                         <button className="btn btn-sm btn-danger" 
//                                             // INYECCIÓN DE NOMBRE DINÁMICO
//                                             onClick={() => triggerModal(`¿Deseas descartar los cambios y regresar a los colores originales del preset "${activePreset.name}"?`, handleRestoreDefault, 'Sí, descartar cambios')}>
//                                             Descartar cambios
//                                         </button>
//                                     )}
//                                 </div>
//                             </div>
//                         </div>

//                         <div className="card-body p-0">
//                             <table className="table table-bordered table-hover mb-0 align-middle text-center">
//                                 <thead className="table-light">
//                                     <tr>
//                                         <th style={{width: '60px'}}>Orden</th>
//                                         <th>Color de pantalla</th>
//                                         <th>¿Flash prendido o apagado?</th>
//                                         <th style={{width: '60px'}}></th>
//                                     </tr>
//                                 </thead>
//                                 <tbody>
//                                     {animationFrames.map((frame, index) => (
//                                         <tr key={frame.id}>
//                                             <td>
//                                                 <div className="d-flex flex-column align-items-center">
//                                                     <button className="btn btn-sm text-muted p-0 border-0" onClick={() => moveFrameUp(index)} disabled={index===0}><i className="bi bi-caret-up-fill"></i></button>
//                                                     <span className="font-weight-bold small">{index + 1}</span>
//                                                     <button className="btn btn-sm text-muted p-0 border-0" onClick={() => moveFrameDown(index)} disabled={index===animationFrames.length-1}><i className="bi bi-caret-down-fill"></i></button>
//                                                 </div>
//                                             </td>
//                                             <td>
//                                                 <div className="d-flex justify-content-center align-items-center">
//                                                     <input 
//                                                         type="color" className="form-control form-control-color border-0 p-0 me-2" 
//                                                         value={frame.color} onChange={(e) => updateFrame(frame.id, 'color', e.target.value)} 
//                                                         style={{ width: '40px', height: '30px' }} 
//                                                     />
//                                                     <span className="small text-muted font-monospace">{frame.color.toUpperCase()}</span>
//                                                 </div>
//                                             </td>
//                                             <td>
//                                                 <div className="form-check form-switch d-flex justify-content-center">
//                                                     <input 
//                                                         className="form-check-input" type="checkbox" checked={frame.flashOn} 
//                                                         onChange={(e) => updateFrame(frame.id, 'flashOn', e.target.checked)} 
//                                                         style={{ cursor: 'pointer', width: '40px', height: '20px' }} 
//                                                     />
//                                                 </div>
//                                             </td>
//                                             <td>
//                                                 <button className="btn btn-sm btn-outline-danger" onClick={() => removeFrame(frame.id)} title="Borrar Frame">
//                                                     <i className="bi bi-trash3-fill"></i>
//                                                 </button>
//                                             </td>
//                                         </tr>
//                                     ))}
//                                 </tbody>
//                             </table>
//                         </div>
                        
//                         <div className="card-footer bg-light">
//                             <button className="btn btn-sm btn-outline-success font-weight-bold mb-3" onClick={addFrame}>
//                                 <i className="bi bi-plus-circle-fill me-1"></i> Agregar Frame
//                             </button>

//                             <hr className="my-2"/>

//                             <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
//                                 <span className="text-muted small font-weight-bold">Cambiar a:</span>
                                
//                                 {/* SELECTOR HÍBRIDO (Botones si <=10, Lista si >10) */}
//                                 {isLoadingPresets ? (
//                                     <span className="small text-muted">Cargando de BD...</span>
//                                 ) : presetsDb.length <= 10 ? (
//                                     presetsDb.map(p => (
//                                         <button 
//                                             key={p.id} 
//                                             className={`btn btn-sm ${activePreset?.id === p.id ? 'btn-primary' : 'btn-outline-secondary'}`}
//                                             onClick={() => applyPresetToUI(p)}
//                                         >
//                                             {p.name} {/*p.isSystemPreset && <i className="bi bi-lock-fill ms-1 opacity-50" title="Preset del Sistema"></i>*/}
//                                         </button>
//                                     ))
//                                 ) : (
//                                     <select 
//                                         className="form-select form-select-sm w-auto" 
//                                         value={activePreset?.id || ''} 
//                                         onChange={(e) => applyPresetToUI(presetsDb.find(p => p.id === parseInt(e.target.value)))}
//                                     >
//                                         <option value="" disabled>-- Selecciona un Preset --</option>
//                                         {presetsDb.map(p => (
//                                             <option key={p.id} value={p.id}>{p.name} {p.isSystemPreset ? '(Sistema)' : ''}</option>
//                                         ))}
//                                     </select>
//                                 )}
//                             </div>

//                             {/* BOTONES DE PELIGRO Y RESTAURACIÓN */}
//                             <div className="d-flex flex-wrap gap-2 mt-3">
//                                 <button className="btn btn-sm btn-light font-weight-bold" onClick={handleSaveAsNew}>
//                                     <i className="bi bi-plus-circle text-primary me-1"></i> Crear preset personalizado
//                                 </button>

//                                 {activePreset && !activePreset.isSystemPreset && (
//                                     <button className="btn btn-sm btn-outline-danger ms-auto" 
//                                         // INYECCIÓN DE NOMBRE DINÁMICO
//                                         onClick={() => triggerModal(`¿Estás seguro de eliminar permanentemente el preset "${activePreset.name}"?`, handleDeletePreset, 'Eliminar preset')}>
//                                         <i className="bi bi-trash"></i> Eliminar preset
//                                     </button>
//                                 )}
//                             </div>
//                         </div>
//                     </div>
//                 </div>

//                 <div className="col-md-5 mb-4">
//                     <div className="card shadow-sm h-100 border-primary">
//                         <div className="card-header bg-primary text-white font-weight-bold">
//                             <i className="bi bi-joystick me-2"></i> 2. Controles Maestros
//                         </div>
//                         <div className="card-body d-flex flex-column">
//                             <div className="alert alert-primary bg-light border-primary pb-3 pt-3 mb-4 shadow-sm">
//                                 <h6 className="font-weight-bold text-primary text-center mb-3">Indicaciones</h6>
//                                 <ol className="mb-0 small text-dark pl-3">
//                                     <li className="mb-2"><strong>Iniciar:</strong> Pide permisos automáticamente a los celulares y comienza el parpadeo al ritmo seleccionado en un solo paso.</li>
//                                     <li><strong>Detener:</strong> Apaga los flashes y libera las cámaras de los usuarios.</li>
//                                 </ol>
//                             </div>

//                             <button className="btn btn-success mb-4 btn-lg font-weight-bold shadow" 
//                                 onClick={() => triggerModal('¿Deseas iniciar/actualizar el show de luces con los BPM y colores actuales?', executeQuickStartShow, 'Sí, iniciar / actualizar')}>
//                                 <i className="bi bi-lightning-charge-fill me-1"></i> Iniciar / Actualizar show
//                             </button>

//                             <button className="btn btn-danger font-weight-bold w-100" 
//                                 onClick={() => triggerModal('¿Detener el show y apagar las luces?', executeStopShow, 'Sí, detener show')}>
//                                 <i className="bi bi-stop-circle-fill me-1"></i> Detener / Apagar Luces
//                             </button>

//                             {/* <hr className="w-100 mt-auto" />

//                             <button className="btn btn-danger font-weight-bold w-100" 
//                                 onClick={() => triggerModal('¿Detener el show y apagar las luces?', executeStopShow, 'Sí, detener show')}>
//                                 <i className="bi bi-stop-circle-fill me-1"></i> Detener / Apagar Luces
//                             </button> */}
//                         </div>
//                     </div>
//                 </div>
//             </div>

//             {showConfirmModal && (
//                 <Confirm 
//                     message={confirmModalData?.message} 
//                     actionToYesButton={confirmModalData?.actionToYesButton} 
//                     textYesButton={confirmModalData?.textYesButton} 
//                     textNoButton={confirmModalData?.textNoButton} 
//                     setShowModal={setShowConfirmModal} 
//                     setConfirmData={setConfirmModalData} 
//                 />
//             )}
//         </SecondaryBase>
//     )
// }

// export { LightShow };